import React, { useState, useEffect } from 'react';
import { commissionService } from '../../services/api';

function CommissionsManagement() {
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCommission, setEditingCommission] = useState(null);
  const [formData, setFormData] = useState({
    recipient_id: '',
    recipient_type: '',
    amount: '',
    type: '',
    description: '',
  });

  useEffect(() => {
    loadCommissions();
  }, []);

  const loadCommissions = async () => {
    try {
      const data = await commissionService.index();
      setCommissions(data.data || []);
    } catch (error) {
      console.error('Error loading commissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCommission) {
        await commissionService.update(editingCommission.id, formData);
      } else {
        await commissionService.store(formData);
      }
      setShowModal(false);
      setEditingCommission(null);
      setFormData({ recipient_id: '', recipient_type: '', amount: '', type: '', description: '' });
      loadCommissions();
    } catch (error) {
      console.error('Error saving commission:', error);
    }
  };

  const handleEdit = (commission) => {
    setEditingCommission(commission);
    setFormData({
      recipient_id: commission.recipient_id,
      recipient_type: commission.recipient_type,
      amount: commission.amount,
      type: commission.type,
      description: commission.description,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this commission?')) {
      try {
        await commissionService.destroy(id);
        loadCommissions();
      } catch (error) {
        console.error('Error deleting commission:', error);
      }
    }
  };

  const handleApprove = async (id) => {
    try {
      await commissionService.approve(id);
      loadCommissions();
    } catch (error) {
      console.error('Error approving commission:', error);
    }
  };

  const handleReject = async (id) => {
    try {
      await commissionService.reject(id);
      loadCommissions();
    } catch (error) {
      console.error('Error rejecting commission:', error);
    }
  };

  const handlePay = async (id) => {
    try {
      await commissionService.pay(id);
      loadCommissions();
    } catch (error) {
      console.error('Error paying commission:', error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Commissions Management</h2>
        <button
          onClick={() => setShowModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
        >
          Add Commission
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recipient</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {commissions.map((commission) => (
              <tr key={commission.id}>
                <td className="px-6 py-4 whitespace-nowrap">{commission.recipient_id} ({commission.recipient_type})</td>
                <td className="px-6 py-4 whitespace-nowrap">{commission.type}</td>
                <td className="px-6 py-4 whitespace-nowrap">${commission.amount}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(commission.status)}`}>
                    {commission.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap space-x-2">
                  <button
                    onClick={() => handleEdit(commission)}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    Edit
                  </button>
                  {commission.status === 'pending' && (
                    <>
                      <button
                        onClick={() => handleApprove(commission.id)}
                        className="text-green-600 hover:text-green-900"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleReject(commission.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Reject
                      </button>
                    </>
                  )}
                  {commission.status === 'approved' && (
                    <button
                      onClick={() => handlePay(commission.id)}
                      className="text-purple-600 hover:text-purple-900"
                    >
                      Pay
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(commission.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {commissions.length === 0 && (
          <div className="text-center py-8 text-gray-500">No commissions found</div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingCommission ? 'Edit Commission' : 'Add Commission'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Recipient ID</label>
                <input
                  type="text"
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                  value={formData.recipient_id}
                  onChange={(e) => setFormData({ ...formData, recipient_id: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Recipient Type</label>
                <select
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                  value={formData.recipient_type}
                  onChange={(e) => setFormData({ ...formData, recipient_type: e.target.value })}
                >
                  <option value="">Select Type</option>
                  <option value="dealer">Dealer</option>
                  <option value="distributor">Distributor</option>
                  <option value="sales_agent">Sales Agent</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Amount</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Type</label>
                <select
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                >
                  <option value="">Select Type</option>
                  <option value="sales">Sales Commission</option>
                  <option value="referral">Referral Bonus</option>
                  <option value="performance">Performance Bonus</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingCommission(null);
                    setFormData({ recipient_id: '', recipient_type: '', amount: '', type: '', description: '' });
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingCommission ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CommissionsManagement;
